// TelcoTrack — weld-layout v2 keys, signatures, deterministic draft IDs.

import { isCompleteCableFiberPair, rozvadecFiberHopKey } from './rozvadec-weld-count';
import type {
  RozvadecWeldCassette,
  RozvadecWeldIncomingRef,
  RozvadecWeldLayout,
  RozvadecWeldLink,
  RozvadecWeldTargetRef,
  RozvadecWeldTermination,
} from './rozvadec-weld-layout.types';

export const ROZVADEC_WELD_CASSETTE_ID_PREFIX = 'wl2-c:';
export const ROZVADEC_WELD_TERMINATION_ID_PREFIX = 'wl2-t:';
export const ROZVADEC_WELD_LINK_ID_PREFIX = 'wl2-l:';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const LINK_KIND_ORDER: Record<string, number> = { direct: 0, connector: 1, splitter: 2 };

export function trimId(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function isPersistedWeldLayoutId(id: string | null | undefined): boolean {
  return UUID_RE.test((id ?? '').trim());
}

export function rozvadecWeldIncomingKey(cableId: string, physicalFiber: number): string {
  return rozvadecFiberHopKey(cableId, physicalFiber);
}

export function rozvadecWeldIncomingRefKey(ref: RozvadecWeldIncomingRef): string {
  return rozvadecWeldIncomingKey(ref.cableId, ref.physicalFiber);
}

export function rozvadecWeldTargetKey(target: RozvadecWeldTargetRef): string {
  return `${target.buildingId.trim()}#${target.cableId.trim()}#${target.physicalFiber}`;
}

export function rozvadecWeldSplitterOutputKey(terminationId: string, splitterOutput: number): string {
  return `${terminationId.trim()}#${splitterOutput}`;
}

/** `sectionId#position` — one rack cell. Span-2 cassettes cover two keys. */
export function rozvadecWeldRackPositionKey(sectionId: string, position: number): string {
  return `${sectionId.trim()}#${position}`;
}

/** `cassetteId#slot` — one adapter / preinstalled-splitter slot. */
export function rozvadecWeldCassetteSlotKey(cassetteId: string, slotInCassette: number): string {
  return `${cassetteId.trim()}#${slotInCassette}`;
}

/** Narrower than the stored ref so a failed guard does not collapse to `never`. */
export type CompleteWeldIncomingRef = RozvadecWeldIncomingRef & { readonly complete: true };
export type CompleteWeldTargetRef = RozvadecWeldTargetRef & { readonly complete: true };

export function isCompleteWeldIncomingRef(
  ref: RozvadecWeldIncomingRef | null | undefined,
): ref is CompleteWeldIncomingRef {
  return !!ref && isCompleteCableFiberPair(ref.cableId, ref.physicalFiber);
}

export function isCompleteWeldTargetRef(
  target: RozvadecWeldTargetRef | null | undefined,
): target is CompleteWeldTargetRef {
  return (
    !!target &&
    target.buildingId.trim().length > 0 &&
    isCompleteCableFiberPair(target.cableId, target.physicalFiber)
  );
}

/** True when the termination sits in a cassette slot (not loose). */
export function hasWeldCassetteSlot(
  termination: Pick<RozvadecWeldTermination, 'cassetteId' | 'slotInCassette'>,
): boolean {
  return (
    trimId(termination.cassetteId).length > 0 &&
    termination.slotInCassette != null &&
    Number.isInteger(termination.slotInCassette)
  );
}

export function rozvadecWeldLinkSourceKey(link: Pick<
  RozvadecWeldLink,
  'kind' | 'incoming' | 'terminationId' | 'splitterOutput'
>): string {
  if (link.kind === 'direct' && link.incoming && isCompleteWeldIncomingRef(link.incoming)) {
    return `direct:${rozvadecWeldIncomingRefKey(link.incoming)}`;
  }
  const terminationId = (link.terminationId ?? '').trim();
  if (link.kind === 'connector' && terminationId) {
    return `connector:${terminationId}`;
  }
  if (
    link.kind === 'splitter' &&
    terminationId &&
    link.splitterOutput != null &&
    Number.isInteger(link.splitterOutput)
  ) {
    return `splitter:${rozvadecWeldSplitterOutputKey(terminationId, link.splitterOutput)}`;
  }
  return `${link.kind}:incomplete`;
}

export function isUnpairedWeldSplitter(
  termination: Pick<RozvadecWeldTermination, 'kind' | 'incoming'>,
): boolean {
  return termination.kind === 'splitter' && !isCompleteWeldIncomingRef(termination.incoming);
}

export function rozvadecWeldCassetteSignature(
  cassette: Pick<RozvadecWeldCassette, 'cassetteType' | 'sectionId' | 'position'>,
): string {
  return `${cassette.cassetteType}:${cassette.sectionId.trim()}:${cassette.position}`;
}

function terminationCassetteRefKey(
  termination: Pick<RozvadecWeldTermination, 'cassetteId' | 'slotInCassette'>,
): string {
  if (!hasWeldCassetteSlot(termination)) return 'loose';
  return rozvadecWeldCassetteSlotKey(termination.cassetteId as string, termination.slotInCassette as number);
}

export function rozvadecWeldTerminationSignature(
  termination: Pick<
    RozvadecWeldTermination,
    'kind' | 'cassetteId' | 'slotInCassette' | 'incoming' | 'splitterRatio'
  >,
): string {
  const incomingKey = isCompleteWeldIncomingRef(termination.incoming)
    ? rozvadecWeldIncomingRefKey(termination.incoming)
    : 'unpaired';
  const ratio = termination.kind === 'splitter' ? (termination.splitterRatio ?? '').trim() || '-' : '-';
  return `${termination.kind}:${terminationCassetteRefKey(termination)}:${incomingKey}:${ratio}`;
}

function linkCassetteId(link: Pick<RozvadecWeldLink, 'cassetteId'>): string {
  return trimId(link.cassetteId);
}

function linkPeerKey(link: Pick<RozvadecWeldLink, 'peerIncoming'>): string {
  return isCompleteWeldIncomingRef(link.peerIncoming)
    ? rozvadecWeldIncomingRefKey(link.peerIncoming)
    : 'none';
}

export function rozvadecWeldLinkSignature(
  link: Pick<
    RozvadecWeldLink,
    'kind' | 'incoming' | 'peerIncoming' | 'cassetteId' | 'seat' | 'terminationId' | 'splitterOutput' | 'target'
  >,
): string {
  const seat = typeof link.seat === 'number' && Number.isInteger(link.seat) && link.seat >= 1
    ? `#s${link.seat}`
    : '';
  const targetKey = isCompleteWeldTargetRef(link.target)
    ? rozvadecWeldTargetKey(link.target)
    : `cas:${linkCassetteId(link)}${seat}#${linkPeerKey(link)}`;
  return `${rozvadecWeldLinkSourceKey(link)}->${targetKey}`;
}

export function deterministicWeldCassetteId(
  cassette: Pick<RozvadecWeldCassette, 'cassetteType' | 'sectionId' | 'position'>,
): string {
  return `${ROZVADEC_WELD_CASSETTE_ID_PREFIX}${rozvadecWeldCassetteSignature(cassette)}`;
}

export function deterministicWeldTerminationId(
  termination: Pick<
    RozvadecWeldTermination,
    'kind' | 'cassetteId' | 'slotInCassette' | 'incoming' | 'splitterRatio'
  >,
): string {
  return `${ROZVADEC_WELD_TERMINATION_ID_PREFIX}${rozvadecWeldTerminationSignature(termination)}`;
}

export function deterministicWeldLinkId(
  link: Pick<
    RozvadecWeldLink,
    'kind' | 'incoming' | 'peerIncoming' | 'cassetteId' | 'seat' | 'terminationId' | 'splitterOutput' | 'target'
  >,
): string {
  return `${ROZVADEC_WELD_LINK_ID_PREFIX}${rozvadecWeldLinkSignature(link)}`;
}

export function resolveWeldCassetteId(
  cassette: Pick<RozvadecWeldCassette, 'id' | 'cassetteType' | 'sectionId' | 'position'>,
): string {
  const existing = trimId(cassette.id);
  if (existing) return existing;
  return deterministicWeldCassetteId(cassette);
}

export function resolveWeldTerminationId(
  termination: Pick<
    RozvadecWeldTermination,
    'id' | 'kind' | 'cassetteId' | 'slotInCassette' | 'incoming' | 'splitterRatio'
  >,
): string {
  const existing = trimId(termination.id);
  if (existing) return existing;
  return deterministicWeldTerminationId(termination);
}

export function resolveWeldLinkId(
  link: Pick<
    RozvadecWeldLink,
    | 'id'
    | 'kind'
    | 'incoming'
    | 'peerIncoming'
    | 'cassetteId'
    | 'seat'
    | 'terminationId'
    | 'splitterOutput'
    | 'target'
  >,
): string {
  const existing = trimId(link.id);
  if (existing) return existing;
  return deterministicWeldLinkId(link);
}

export function compareWeldCassettes(a: RozvadecWeldCassette, b: RozvadecWeldCassette): number {
  const section = a.sectionId.localeCompare(b.sectionId);
  if (section !== 0) return section;
  if (a.position !== b.position) return a.position - b.position;
  return a.id.localeCompare(b.id);
}

export function compareWeldTerminations(a: RozvadecWeldTermination, b: RozvadecWeldTermination): number {
  const cassette = terminationCassetteRefKey(a).localeCompare(terminationCassetteRefKey(b));
  if (cassette !== 0) return cassette;
  const incoming = weldIncomingSortKey(a.incoming).localeCompare(weldIncomingSortKey(b.incoming));
  if (incoming !== 0) return incoming;
  if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
  return a.id.localeCompare(b.id);
}

export function compareWeldLinks(a: RozvadecWeldLink, b: RozvadecWeldLink): number {
  const kind = (LINK_KIND_ORDER[a.kind] ?? 9) - (LINK_KIND_ORDER[b.kind] ?? 9);
  if (kind !== 0) return kind;
  const source = rozvadecWeldLinkSourceKey(a).localeCompare(rozvadecWeldLinkSourceKey(b));
  if (source !== 0) return source;
  const target = rozvadecWeldLinkSignature(a).localeCompare(rozvadecWeldLinkSignature(b));
  if (target !== 0) return target;
  return a.id.localeCompare(b.id);
}

function weldIncomingSortKey(incoming: RozvadecWeldIncomingRef | null | undefined): string {
  return isCompleteWeldIncomingRef(incoming) ? rozvadecWeldIncomingRefKey(incoming) : '';
}

export function sortWeldLayout(layout: RozvadecWeldLayout): RozvadecWeldLayout {
  const weldConnections = [...(layout.weldConnections ?? [])].sort((a, b) => a.id.localeCompare(b.id));
  const patchConnections = [...(layout.patchConnections ?? [])].sort((a, b) => a.id.localeCompare(b.id));
  const splitterPaths = [...(layout.splitterPaths ?? [])].sort((a, b) => a.id.localeCompare(b.id));
  return {
    version: layout.version,
    offers: { ...layout.offers },
    cassettes: [...layout.cassettes].sort(compareWeldCassettes),
    terminations: [...layout.terminations].sort(compareWeldTerminations),
    links: [...layout.links].sort(compareWeldLinks),
    ...(weldConnections.length ? { weldConnections } : {}),
    ...(patchConnections.length ? { patchConnections } : {}),
    ...(splitterPaths.length ? { splitterPaths } : {}),
  };
}

function occupancyPairKey(
  rows: ReadonlyArray<{ fromEndpointId?: string; toEndpointId?: string }> | undefined,
): string {
  return (rows ?? [])
    .map((row) => `${String(row.fromEndpointId ?? '')}>${String(row.toEndpointId ?? '')}`)
    .sort((a, b) => a.localeCompare(b))
    .join(',');
}

function splitterPathKey(
  rows: ReadonlyArray<{
    cassetteId?: string;
    splitterIndex?: number;
    inputPatchId?: string | null;
    outputPatches?: ReadonlyArray<{ output: number; patchId: string | null }>;
  }> | undefined,
): string {
  return (rows ?? [])
    .map((row) => {
      const outputs = (row.outputPatches ?? [])
        .map((item) => `${item.output}:${item.patchId ?? ''}`)
        .join(',');
      return `${row.cassetteId ?? ''}:${row.splitterIndex ?? 0}:${row.inputPatchId ?? ''}:${outputs}`;
    })
    .sort((a, b) => a.localeCompare(b))
    .join('|');
}

/** Structural persist fingerprint — ignores row UUIDs so a no-op rewrite is detectable. */
export function rozvadecWeldLayoutPersistSignature(layout: RozvadecWeldLayout): string {
  const sorted = sortWeldLayout(layout);
  return [
    `o:${sorted.offers.directEnabled ? 1 : 0}${sorted.offers.connectorsEnabled ? 1 : 0}`,
    `c:${sorted.cassettes.map((item) => rozvadecWeldCassetteSignature(item)).join(',')}`,
    `t:${sorted.terminations.map((item) => rozvadecWeldTerminationSignature(item)).join(',')}`,
    `l:${sorted.links.map((item) => rozvadecWeldLinkSignature(item)).join(',')}`,
    `w:${occupancyPairKey(sorted.weldConnections)}`,
    `p:${occupancyPairKey(sorted.patchConnections)}`,
    `s:${splitterPathKey(sorted.splitterPaths)}`,
  ].join(';');
}

export function weldLayoutsPersistEquivalent(
  left: RozvadecWeldLayout,
  right: RozvadecWeldLayout,
): boolean {
  return rozvadecWeldLayoutPersistSignature(left) === rozvadecWeldLayoutPersistSignature(right);
}
